import { Box, Icon, Typography } from "@husqvarna/ui-core";
import "./FormTextWithSuffix.scss";
import React, { forwardRef, useCallback, useState } from "react";
import { InputHTMLAttributes } from "react";
import clsx from "clsx";
import { UseFormRegisterReturn } from "react-hook-form";

interface SuffixOptions {
  label: string;
  value: string;
}

interface FormTextWithSuffixProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange"> {
  placeholder?: string;
  suffixOptions?: SuffixOptions[];
  suffix?: string;
  onSuffixChange?: (value: string) => void;
  fitContent?: boolean;
  error?: string;
  value?: string | number;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
  allowDecimals?: boolean;
  disabled?: boolean;
  suffixRegister?: UseFormRegisterReturn;
}

const FormTextWithSuffix = forwardRef<
  HTMLInputElement,
  FormTextWithSuffixProps
>(
  (
    {
      placeholder = undefined,
      suffixOptions = [],
      suffix,
      fitContent = true,
      error,
      value,
      onChange,
      className = "",
      id,
      allowDecimals = true,
      disabled = false,
      suffixRegister,
      ...rest
    },
    ref
  ) => {
    const hasOptions = suffixOptions.length > 0 && suffix !== undefined;
    // Generate a unique ID once per component instance, if not provided.
    const [generatedId] = useState(
      () => `form-text-with-suffix-${Math.random().toString(36).substring(2, 11)}`
    );
    const uniqueId = id || generatedId;
    const errorId = `${uniqueId}-error`;

    const isControlled = value !== undefined;
    const stringValue = value?.toString() || "";

    // Handle input changes with integer-only filtering if needed
    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (allowDecimals === false) {
          if (!/^-?\d*$/.test(e.target.value)) {
            e.preventDefault();
            return;
          }
        }
        onChange?.(e);
      },
      [allowDecimals, onChange]
    );

    return (
      <Box container flexDirection="column">
        <div
          className={clsx(
            "form-text-with-suffix",
            className, // Correctly pass className
            {
              "fit-content": fitContent,
              "error": !!error,
              "disabled": disabled,
            }
          )}
        >
          <input
            ref={ref}
            id={uniqueId}
            type="text"
            inputMode={allowDecimals ? "decimal" : "numeric"}
            placeholder={placeholder}
            aria-invalid={!!error}
            aria-describedby={error ? errorId : undefined}
            disabled={disabled}
            {...rest}
            {...(isControlled
              ? { value: stringValue, onChange: handleInputChange }
              : { onChange: handleInputChange })}
          />
          {!hasOptions && suffix && (
            <>
            <div className="tcsp-divider space"></div>
            <Typography tag="p" variant="Body1" className="suffix">
              {suffix}
            </Typography>
            </>
          )}
          {hasOptions && (
            <>
              <div className="tcsp-divider"></div>
              <div className="suffix-container">
                <select
                  {...suffixRegister}
                  id={`${uniqueId}-suffix`}
                  value={suffix}
                  disabled={disabled}
                  aria-label="Suffix select"
                >
                  {suffixOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <label htmlFor={`${uniqueId}-suffix`} className="suffix-label">
                  <Icon
                    size={10}
                    iconId="angle-down"
                    aria-label="Open dropdown"
                  />
                </label>
              </div>
            </>
          )}
        </div>
        {error && (
          <Typography tag="p" variant="Microcopy" mt={1} color="ErrorRed" text={error} />
        )}
      </Box>
    );
  }
);

FormTextWithSuffix.displayName = "FormTextWithSuffix";

export default FormTextWithSuffix;
